import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const I18nContext = createContext();

// Common translations
const translations = {
  en: {
    home: 'HOME',
    aboutUs: 'ABOUT US',
    research: 'RESEARCH',
    publications: 'PUBLICATIONS',
    technologies: 'TECHNOLOGIES',
    varieties: 'VARIETIES',
    forFarmers: 'FOR FARMERS',
    nursery: 'NURSERY',
    media: 'MEDIA',
    contactUs: 'CONTACT US',
    search: 'Search...',
    skipToContent: 'Skip to main content',
    fromDirectorsDesk: "FROM DIRECTOR'S DESK",
    ourTeam: 'OUR TEAM',
    ourOrganization: 'OUR ORGANIZATION',
    ourPerformance: 'OUR PERFORMANCE',
    seeMore: 'SEE MORE',
    viewMore: 'VIEW MORE',
    readMore: 'Read More',
    varietiesForCommercialization: 'VARIETIES FOR COMMERCIALIZATION',
    technologiesForCommercialization: 'TECHNOLOGIES FOR COMMERCIALIZATION',
    keyImpacts: 'KEY IMPACTS',
    keyResearch: 'KEY RESEARCH',
    keyOfferings: 'KEY OFFERINGS',
    announcements: 'Announcements',
    jobs: 'Jobs',
    tenders: 'Tenders',
    usefulLinks: 'USEFUL LINKS',
    newsAndEvents: 'NEWS & EVENTS',
    inSocialMedia: 'IN SOCIAL MEDIA',
    quickLinks: 'Quick Links',
    contactInformation: 'CONTACT INFORMATION',
    services: 'Services',
    privacyPolicy: 'Privacy Policy',
    termsOfUse: 'Terms of Use',
    sitemap: 'Sitemap',
    allRightsReserved: 'All Rights Reserved',
    new: 'NEW',
    pioneers: 'WE ARE PIONEERS IN SUBTROPICAL FRUIT RESEARCH SPANNING FROM CLIMATE RESILIENT VARIETIES TO VALUE CHAIN RESEARCH.',
    viewAnnouncements: 'View Announcements',
    lastDate: 'Last Date',
    title: 'Title',
    sNo: 'S. No'
  },
  hi: {
    home: 'होम',
    aboutUs: 'हमारे बारे में',
    research: 'अनुसंधान',
    publications: 'प्रकाशन',
    technologies: 'प्रौद्योगिकियां',
    varieties: 'किस्में',
    forFarmers: 'किसानों के लिए',
    nursery: 'नर्सरी',
    media: 'मीडिया',
    contactUs: 'संपर्क करें',
    search: 'खोजें...',
    skipToContent: 'मुख्य सामग्री पर जाएं',
    fromDirectorsDesk: 'निदेशक की कलम से',
    ourTeam: 'हमारी टीम',
    ourOrganization: 'हमारा संगठन',
    ourPerformance: 'हमारा प्रदर्शन',
    seeMore: 'और देखें',
    viewMore: 'और देखें',
    readMore: 'और पढ़ें',
    varietiesForCommercialization: 'व्यावसायीकरण के लिए किस्में',
    technologiesForCommercialization: 'व्यावसायीकरण के लिए प्रौद्योगिकियां',
    keyImpacts: 'मुख्य प्रभाव',
    keyResearch: 'मुख्य अनुसंधान',
    keyOfferings: 'मुख्य सेवाएं',
    announcements: 'घोषणाएं',
    jobs: 'नौकरियां',
    tenders: 'निविदाएं',
    usefulLinks: 'उपयोगी लिंक',
    newsAndEvents: 'समाचार एवं आयोजन',
    inSocialMedia: 'सोशल मीडिया में',
    quickLinks: 'त्वरित लिंक',
    contactInformation: 'संपर्क जानकारी',
    services: 'सेवाएं',
    privacyPolicy: 'गोपनीयता नीति',
    termsOfUse: 'उपयोग की शर्तें',
    sitemap: 'साइटमैप',
    allRightsReserved: 'सर्वाधिकार सुरक्षित',
    new: 'नया',
    pioneers: 'हम उपोष्ण कटिबंधीय फल अनुसंधान में अग्रणी हैं जो जलवायु प्रतिरोधी किस्मों से लेकर मूल्य श्रृंखला अनुसंधान तक फैला हुआ है।',
    viewAnnouncements: 'घोषणाएं देखें',
    lastDate: 'अंतिम तिथि',
    title: 'शीर्षक',
    sNo: 'क्र.सं.'
  }
};

export function I18nProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('cish-lang') || 'en';
  });

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('cish-lang', lang);
    document.documentElement.lang = lang;
  }, []);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

export default useI18n;
